import { z } from 'zod';

export const BadgeTierSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
]);

export const BadgeBlockSchema = z.enum([
  'block-1',
  'block-2',
  'block-3',
  'block-4',
  'cross-block',
]);

export const RankTypeSchema = z.enum([
  'detective',
  'author',
  'ctfl',
  'api-master',
  'sql-detective',
  'security-watcher',
  'automation-architect',
  'ci-wrangler',
  'ai-qa-master',
]);

export const BadgeTriggerSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('sprint-required-complete'), sprintNum: z.number().int() }),
  z.object({ kind: z.literal('sprint-bonus-complete'), sprintNum: z.number().int() }),
  z.object({ kind: z.literal('release-unlock'), releaseVersion: z.string() }),
  z.object({
    kind: z.literal('rank-threshold'),
    rankType: RankTypeSchema,
    threshold: z.number().int(),
  }),
  z.object({ kind: z.literal('cumulative-bonus-count'), threshold: z.number().int() }),
  z.object({ kind: z.literal('capstone-bonus-complete'), capstoneId: z.string() }),
  z.object({
    kind: z.literal('all-senior-extensions-opened'),
    sprintNum: z.number().int().optional(),
    block: BadgeBlockSchema.optional(),
  }),
  z.object({ kind: z.literal('theme-locale-polyglot') }),
  z.object({
    kind: z.literal('pomodoro-time-window'),
    window: z.enum(['early-bird', 'night-owl']),
    threshold: z.number().int(),
  }),
  z.object({ kind: z.literal('first-quick-check-correct') }),
  z.object({ kind: z.literal('all-glossary-mastered'), threshold: z.number().int() }),
  /**
   * W4.1 extension 2026-05-04 · per Genie ack Q6=A · CF-W3.1-03 unblock.
   * Generic capability-flag trigger · fires когда `progress.achievements.capabilityFlags[flag] === true`.
   * Used by Tier 4 curiosity badges (Bug Hunter B2 · First API Request · Hacker Mindset ·
   * Polyglot B2 · Selenium Recorder · Flake Slayer · Coverage Champion · Prompt Injector ·
   * Bias Detector · Shipped v2.0 · Career Launch S18) — 11 badges total Block 2-4.
   * Optional threshold `count` для cumulative-style flags (e.g., «3+ scenarios with prompt-injection found»).
   */
  z.object({
    kind: z.literal('capability-flag'),
    flag: z.string(),
    /** Required count для cumulative flags · default 1 (single-flip). */
    threshold: z.number().int().min(1).optional(),
  }),
]);

export const BadgePlacementSchema = z.enum([
  'topbar-rank-chip',
  'profile-wall-hero',
  'profile-wall-section',
  'sprint-card-shimmer',
  'sprint-review-ceremony',
  'capstone-ceremony',
  'istqb-path-widget',
  'toast-notification',
  'dashboard-mini-lesson-widget',
]);

export const BadgeDefinitionSchema = z.object({
  id: z.string(),
  name: z.string(),
  emoji: z.string(),
  /**
   * Ключ рисованной SVG-иконки в реестре `components/BadgeIcon` (редизайн наград
   * 25 JUN: уход от эмодзи к ink/riso-глифам). Optional — пока иконка не нарисована,
   * стена/тост рендерят `emoji` (постепенная миграция по блокам). Когда `icon` задан,
   * рендерится SVG вместо эмодзи.
   */
  icon: z.string().optional(),
  tier: BadgeTierSchema,
  block: BadgeBlockSchema,
  description: z.string(),
  trigger: BadgeTriggerSchema,
  hiddenUntilEarned: z.boolean().optional(),
  uiPlacement: z.array(BadgePlacementSchema),
  skillsCrossRef: z.array(z.string()).optional(),
});

export const EarnedBadgeSchema = z.object({
  badgeId: z.string(),
  earnedAt: z.number(),
  earnedContext: z
    .object({
      sprintNum: z.number().int().optional(),
      scenarioId: z.string().optional(),
      capstoneId: z.string().optional(),
    })
    .optional(),
});

export const AccentOverlaySchema = z.enum(['default', 'rose', 'sage', 'indigo', 'gold']);

export const AccentStateSchema = z.object({
  selected: AccentOverlaySchema,
  unlocked: z.array(AccentOverlaySchema),
});

export const BuddyStateSchema = z.object({
  equippedSkin: z.string(),
  unlockedSkins: z.array(z.string()),
});

export const QuizPackResultSchema = z.object({
  packId: z.string(),
  solvedAt: z.number(),
  scorePct: z.number(),
  perQuestion: z.record(z.string(), z.boolean()),
});

export const MiniLessonsStateSchema = z.object({
  unlocked: z.array(z.string()),
  readAt: z.record(z.string(), z.number()),
  readingProgress: z.record(z.string(), z.number()),
  quizPacksSolved: z.record(z.string(), QuizPackResultSchema),
});

export const PomodoroSessionEventSchema = z.object({
  at: z.number(),
  startHour: z.number().int(),
});

export const AchievementsStateSchema = z.object({
  earned: z.array(EarnedBadgeSchema),
  progress: z.record(z.string(), z.number()),
  hidden: z.array(z.string()),
  buddySkin: BuddyStateSchema,
  accentOverlay: AccentStateSchema,
  miniLessons: MiniLessonsStateSchema,
  pomodoroSessions: z.array(PomodoroSessionEventSchema),
  themeUsage: z.record(z.string(), z.number()),
  localeUsage: z.record(z.string(), z.number()),
  seniorExtensionsOpened: z.record(z.string(), z.boolean()),
  /**
   * W4.1 extension 2026-05-04 · capability flags для Tier 4 curiosity triggers.
   * Map flag-name → counter. Single-flip flags use 0/1 (`prompt-injector-found`
   * goes 0→1 на первом detection). Cumulative flags use counter (`prompt-injector-count`
   * grows as student finds prompt-injection across scenarios). Threshold check в
   * matchTrigger reads counter и compares.
   * Optional · undefined treated as «no flags set».
   */
  capabilityFlags: z.record(z.string(), z.number()).optional(),
  /**
   * Экранное время практики по дням (хаб «Твоя практика» · 24 JUN 2026).
   * dateKey `YYYY-MM-DD` → секунды активной работы (focus/visibility-aware трекер,
   * простой ~90с не считается). Optional · старые сейвы парсятся без него.
   * Питает `activityMinByDay` в `lib/practiceStats.ts` (кольцо дня / хитмап / стрик).
   */
  activityByDay: z.record(z.string(), z.number()).optional(),
  /**
   * Экранное время по ТИКЕТАМ (доска спринта · 11 JUL 2026): ticketId → секунды.
   * Тот же трекер, что activityByDay (useScreenTime), второй срез аккумуляции —
   * пишется, только когда открыт экран тикета. Питает реальные суммы времени
   * в колонках доски (вместо теоретической суммы estimateMin — фидбек Genie).
   * Optional · старые сейвы без него; deployed edge strip'ает ключ до редеплоя
   * (CF-MR-EDGE-REDEPLOY — тот же долг, что fontScale/avatarBuddy).
   */
  activityByTicket: z.record(z.string(), z.number()).optional(),
});

export type BadgeTier = z.infer<typeof BadgeTierSchema>;
export type BadgeBlock = z.infer<typeof BadgeBlockSchema>;
export type RankType = z.infer<typeof RankTypeSchema>;
export type BadgeTrigger = z.infer<typeof BadgeTriggerSchema>;
export type BadgePlacement = z.infer<typeof BadgePlacementSchema>;
export type BadgeDefinition = z.infer<typeof BadgeDefinitionSchema>;
export type EarnedBadge = z.infer<typeof EarnedBadgeSchema>;
export type AccentOverlay = z.infer<typeof AccentOverlaySchema>;
export type AccentState = z.infer<typeof AccentStateSchema>;
export type BuddyState = z.infer<typeof BuddyStateSchema>;
export type QuizPackResult = z.infer<typeof QuizPackResultSchema>;
export type MiniLessonsState = z.infer<typeof MiniLessonsStateSchema>;
export type PomodoroSessionEvent = z.infer<typeof PomodoroSessionEventSchema>;
export type AchievementsState = z.infer<typeof AchievementsStateSchema>;
